/**
 * Effets de bord DOM (application des variables CSS + méta document).
 * Toutes les fonctions sont sûres en SSR (guards `typeof document`).
 */
import type { ColorScheme, TenantBranding } from './types';

/** Applique une map de variables CSS sur `document.documentElement`. */
export function applyCssVars(vars: ColorScheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    if (!key.startsWith('--')) continue;
    root.style.setProperty(key, value);
  }
}

/** Met à jour ou crée une balise `<meta name="...">`. */
function setMetaContent(name: string, content: string): void {
  if (typeof document === 'undefined') return;
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Synchronise `document.title` + `<meta theme-color>` + titre PWA iOS avec le
 * branding résolu. Pour un tenant existant, ces valeurs sont identiques à
 * l'index.html d'origine → aucun changement visible.
 */
export function applyDocumentMeta(branding: TenantBranding): void {
  if (typeof document === 'undefined') return;
  if (branding.displayName) document.title = branding.displayName;
  if (branding.pwa?.themeColor) setMetaContent('theme-color', branding.pwa.themeColor);
  // NB: on utilise displayName (et non shortName) pour rester identique à
  // l'index.html d'origine des apps existantes (ex. Bocuse : « Maisons Bocuse »).
  if (branding.displayName) {
    setMetaContent('apple-mobile-web-app-title', branding.displayName);
  }
  if (branding.pwa?.description) setMetaContent('description', branding.pwa.description);
}
